import * as _ from 'lodash';
import { getField, updateField } from 'vuex-map-fields';
import { types } from './mutation-types';
import { productSummary } from '../../sore-helpers';

import { RepositoryFactory } from '../../../repositories/RepositoryFactory';
import { deliveryObject } from '../../../utils/deliveryObject';
import { buildNdeDeliveryPointsStateFromFile } from '@/utils/delivery-points/nde';

import { filterOutConfectionProduct } from '../../../utils/confection/filters';
import { DELIVERY_POINT_PRODUCT_ID } from '@/utils/constants/delivery-point';
import { checkProductIsNDEPersonalizedConfection } from '@/utils/categoryUtils';

const shop = RepositoryFactory.get('customer');

function getDefaultState() {
  return {
    token: 0,
    delivery_file: null,
    products: [],
    address: [],
    backendDeliveries: [],
    inputMode: null,
  };
}

export const delivery = {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    // eslint-disable-next-line camelcase
    productSummary: state => (delivery_id) => {
      // eslint-disable-next-line camelcase
      const products = state.products.filter(item => Number(item.iddelivery) === Number(delivery_id));
      return productSummary(products);
    },
    // eslint-disable-next-line camelcase
    getDeliveryFile: state => delivery_id => state.address[delivery_id].delivery_file,
    getTotal: state => productSummary(state.products),
    getItemsTotal: (state) => {
      let products;
      let confection;

      if (state.products.length !== 0) {
        products = filterOutConfectionProduct(state.products).productsWithoutConfection;
        confection = filterOutConfectionProduct(state.products).confectionItems;

        const output = _(products)
          .groupBy('price')
          .map((objs, key) => ({
            by: key,
            id: _.get(objs, '[0].id'),
            quantity: _.sumBy(objs, 'quantity'),
            price: _.get(objs, '[0].price'),
            name: _.get(objs, '[0].name'),
            product_category: _.get(objs, '[0].product_category'),
          }))
          .value();

        const groupedConfection = _(confection)
          .groupBy('price')
          .map((objs, key) => ({
            by: key,
            id: _.get(objs, '[0].id'),
            quantity: _.sumBy(objs, 'quantity'),
            price: _.get(objs, '[0].price'),
            name: _.get(objs, '[0].name'),
            product_category: _.get(objs, '[0].product_category'),
          }))
          .value();

        const orderedItems = _.orderBy(output, 'id', 'asc');

        return [
          ...orderedItems,
          ...groupedConfection,
        ];
      }

      return [];
    },
    getItemsAll: state => [...state.products],
    getItemsWithoutConfection: (state) => {
      if (state.products.length === 0) return [];

      const products = filterOutConfectionProduct(state.products).productsWithoutConfection;
      const output = _(products)
        .groupBy('price')
        .map((objs, key) => ({
          by: key,
          id: _.get(objs, '[0].id'),
          quantity: _.sumBy(objs, 'quantity'),
          price: _.get(objs, '[0].price'),
          name: _.get(objs, '[0].name'),
          product_category: _.get(objs, '[0].product_category'),
          iddelivery: _.get(objs, '[0].iddelivery'),
        }))
        .value();
      const orderedItems = _.orderBy(output, 'id', 'asc');
      return orderedItems;
    },
    getItemsWithoutConfectionWithoutGrouping: (state) => {
      if (state.products.length === 0) return [];

      const products = filterOutConfectionProduct(state.products).productsWithoutConfection;
      return products;
    },
    getOnlyConfectionItems: (state) => {
      if (state.products.length === 0) return [];

      const confection = filterOutConfectionProduct(state.products).confectionItems;
      const groupedConfection = _(confection)
        .groupBy('id')
        .map((objs, key) => ({
          by: key,
          id: _.get(objs, '[0].id'),
          quantity: _.sumBy(objs, 'quantity'),
          price: _.get(objs, '[0].price'),
          name: _.get(objs, '[0].name'),
          slug: _.get(objs, '[0].slug'),
          product_category: _.get(objs, '[0].product_category'),
        }))
        .value();
      return groupedConfection;
    },
    getDeliveryPointsCount: (state) => {
      const cartItem = state.products.filter(item => item.id === DELIVERY_POINT_PRODUCT_ID)[0];
      return cartItem ? cartItem.quantity : 1;
    },
    getSelectedNDEPersonalizedConfection: state => state.products.filter(item => checkProductIsNDEPersonalizedConfection(item) && item.quantity > 0),
    hasPersonalizedConfectionInCart: (state, getters) => getters.getSelectedNDEPersonalizedConfection.length > 0,
    getValue: state => (iddelivery, productid) => state.products.filter(item => item.iddelivery === iddelivery && item.id === productid),
    getValueBySlug: state => (iddelivery, productSlug) => state.products.filter(item => item.iddelivery === iddelivery && item.slug === productSlug),
    getItemsByCategory: state => (iddelivery, category) => state.products.filter(item => item.iddelivery === iddelivery && item.product_category === category && item.quantity > 0),
    getTotalProductQuantity: (state, getters) => (productId) => {
      const deliveryIds = [...Array(getters.getDeliveryPointsCount).keys()];
      const total = deliveryIds.reduce((sum, deliveryId) => {
        const products = getters.getValue(deliveryId, productId);
        const quantityToAdd = products.reduce((subSum, product) => subSum + product.quantity, 0);
        return sum + quantityToAdd;
      }, 0);
      return total;
    },
    getItemsCount: state => state.products.length,
    // eslint-disable-next-line camelcase
    getToken: state => delivery_id => state.address[delivery_id].token,
    getField,
  },

  actions: {
    setAddress({ commit }, newAddress) {
      commit('handleSetAddress', newAddress);
    },
    setDeliveryToken({ commit }, object) {
      commit(types.ADD_DELIVERY_TOKEN, object);
    },
    setDeliveryFile({ commit }, object) {
      commit(types.ADD_DELIVERY_FILE, object);
    },
    resetDeliveryFile({ commit }) {
      commit(types.RESET_DELIVERY_FILE);
    },
    setInitAddress({ commit }, newAddress) {
      commit('handleInitAddress', newAddress);
    },
    reset({ commit }) {
      commit('reset');
    },
    addProductToCart({ state, commit }, product) {
      const cartItem = state.products.find(item => (item.id === product.id && item.iddelivery === product.iddelivery));
      if (!cartItem) {
        commit('pushProductToCart', product);
      } else {
        commit('incrementItemQuantity', {
          cartItem,
          product,
        });
      }
    },
    addVirtualCardToCart({ commit }, product) {
      commit('pushProductToCart', product);
    },
    deleteProductFromCart({ state, commit }, props) {
      const index = state.products.findIndex(item => (item.id === props[0].id && item.iddelivery === parseInt(props[1], 10)));
      if (index === -1) return;
      commit(types.REMOVE_ITEM_FROM_CART, index);
    },
    copyDeliveryProducts({ dispatch, state, commit }, props) {
      // eslint-disable-next-line eqeqeq
      const copyTo = state.products.filter(item => item.iddelivery == props[1]);
      // eslint-disable-next-line eqeqeq
      const copyFrom = state.products.filter(item => item.iddelivery == props[0]);

      if (copyTo.length === 0) {
        commit(types.COPY, [copyFrom, props[1]]);
        dispatch('copyAdditional', props, { root: true });
      }
    },
    removeDelivery({ commit }, iddelivery) {
      commit(types.REMOVE_DELIVERY, iddelivery);
    },
    initDelivery({ dispatch, state, commit }, iddelivery) {
      for (let i = state.address.length; i < iddelivery; i++) {
        dispatch('setInitAddress', [deliveryObject, i]);
      }
      commit('cutAddressArray', iddelivery);
    },
    handleStateChange({ commit }, payload) {
      commit(types.ADD_DELIVERY_ADDRESS, payload);
    },
    handleAddDeliveryPerson({ commit }, payload) {
      commit(types.ADD_DELIVERY_CONTECT_PERSON, payload);
    },
    bindBackendDeliveries({ commit }) {
      shop.getBackendDeliveries().then((response) => {
        commit(types.BIND_BACKEND_DELIVERIES, response.data);
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error.message);
      });
    },
    hydrateValidatedDeliveryFile({
      state, commit, dispatch, rootState, rootGetters,
    }, data) {
      const deliveryPoints = Array.isArray(data) ? data : [];
      const deliveryPointsCount = deliveryPoints.length || 1;
      const mainProduct = rootGetters.allProducts[0];
      const deliveryPointProduct = rootGetters.getDeliveryAddressProduct;
      const previousCart = [...state.products];

      dispatch('reset');
      dispatch('resetProduct', null, { root: true });
      dispatch('cart/setDeliveryNumber', deliveryPointsCount, { root: true });

      const { addresses, cartItems, confectionData } = buildNdeDeliveryPointsStateFromFile({
        deliveryPoints,
        mainProduct,
        deliveryPointProduct,
        confectionState: rootState.confection,
        previousCart,
        createAdditionalProductId: (deliveryId) => {
          const additionalProduct = { ...mainProduct };
          dispatch('addProduct', [additionalProduct, deliveryId], { root: true });
          return additionalProduct.idproducts;
        },
      });

      commit(types.SET_FILE_DELIVERIES_STATE, {
        address: addresses,
        products: cartItems,
      });
      dispatch('confection/setConfectionData', confectionData, { root: true });
      return cartItems;
    },
    resetConfectionChoice({ commit }) {
      commit(types.RESET_CONFECTION_CHOICE);
    },
    hydrate({ commit }, state) {
      commit(types.HYDRATE, state);
    },
  },
  mutations: {
    updateField,
    [types.RESET_CONFECTION_CHOICE](state) {
      const { productsWithoutConfection } = filterOutConfectionProduct(state.products);
      state.products = productsWithoutConfection;
    },
    [types.BIND_BACKEND_DELIVERIES](state, payload) {
      state.backendDeliveries = payload;
    },
    pushProductToCart(state, product) {
      state.products.push(product);
    },
    incrementItemQuantity(state, { cartItem, product }) {
      const newCartItem = cartItem;
      newCartItem.quantity = product.quantity;
      newCartItem.price = product.price;
    },
    [types.REMOVE_ITEM_FROM_CART](state, index) {
      state.products.splice(index, 1);
    },
    reset(state) {
      state.address = [deliveryObject];
      state.products = [];
    },
    handleInitAddress(state, props) {
      const newObject = Object.assign({}, props[0]);
      state.address.push(newObject);
    },
    handleSetAddress(state, props) {
      state.address.splice(props[1], 1, props[0]);
    },
    [types.ADD_DELIVERY_TOKEN](state, object) {
      if (state.address[object.index].token === '') {
        state.address[object.index].token = object.token;
      }
    },
    [types.ADD_DELIVERY_FILE](state, object) {
      state.address[object.index].delivery_file = object.fileName;
    },
    [types.RESET_DELIVERY_FILE](state) {
      if (!state.address || state.address.length < 1) return;
      state.address = state.address.map(address => ({
        ...address,
        delivery_file: '',
      }));
    },
    [types.SET_FILE_DELIVERIES_STATE](state, { address, products }) {
      state.address = address;
      state.products = products;
    },
    [types.COPY](state, props) {
      for (let i = 0; i < props[0].length; i++) {
        if (props[0][i].id !== 1000002) {
          const newObject = Object.assign({}, props[0][i]);
          // eslint-disable-next-line prefer-destructuring
          newObject.iddelivery = props[1];
          state.products.push(newObject);
        }
      }
    },
    [types.REMOVE_DELIVERY](state, iddelivery) {
      _.remove(state.products, e => e.iddelivery >= iddelivery);
    },
    [types.ADD_DELIVERY_ADDRESS](state, { prop, value, deliveryId }) {
      state.address = {
        ...state.address,
        [deliveryId]: {
          ...state.address[deliveryId],
          [prop]: value,
        },
      };
    },
    cutAddressArray(state, id) {
      state.address.length = id;
    },
    [types.ADD_DELIVERY_CONTECT_PERSON](state, {
      prop, value, index, deliveryId,
    }) {
      if (!state.address.hasOwnProperty(deliveryId)) {
        state.address[deliveryId] = {};
      }

      if (!state.address[deliveryId].hasOwnProperty('contactPerson')) {
        state.address[deliveryId].contactPerson = {};
      }

      if (!state.address[deliveryId].contactPerson.hasOwnProperty(index)) {
        state.address[deliveryId].contactPerson[index] = {};
      }

      state.address = {
        ...state.address,
        [deliveryId]: {
          ...state.address[deliveryId],
          contactPerson: {
            ...state.address[deliveryId].contactPerson,
            [index]: {
              ...state.address[deliveryId].contactPerson[index],
              [prop]: value,
            },
          },
        },
      };
    },
    [types.HYDRATE](state, payload = {}) {
      Object.assign(state, getDefaultState(), payload);
    },
  },
};
